import React, { useContext, useCallback, useEffect, useState } from 'react';

import { v4 as uuidv4 } from 'uuid';

import { NCRContext } from 'views';
import { UserContext } from 'components/routing';

import Col from 'react-bootstrap/Col';
import Row from 'react-bootstrap/Row';
import FormControl from 'react-bootstrap/FormControl';
import Button from 'react-bootstrap/Button';

import { OrderItem } from 'components/form/prototypes';

const OrderItems = () => {
  const { state, setState } = useContext(NCRContext);
  const { user } = useContext(UserContext);

  const isDisabled =
    !state.ncr.original_so_number ||
    !state.ncr.subsidiary ||
    (['GM'].includes(user.role)
      ? state.ncr.status.id > 4
      : state.ncr.status.id > 1);

  /**
   * FUNCTION: addOrderItem
   * This function adds a new item to the orderItems state
   * The object has a 'html' field where we store the OrderItem component
   * This part is added in the useEffect that checks if the user has
   * added/removed a product/Item
   * The updateItem callback updates this object with the relevant values
   * e.g.
   * {
   *  id: '12345',
   *  item_code: 'Strawberry',
   *  price: '20.00',
   *  quantity: 2,
   *  html: <OrderItem ... />
   * }
   **/
  const addOrderItem = () => {
    setState((prev) => {
      prev.ncr.order.orderItems = [...prev.ncr.order.orderItems, {}];
      return { ...prev };
    });
  };

  /**
   * FUNCTION: updateItem
   * @id of the item we're going to update
   * @event the value of the item
   **/
  const updateItem = useCallback(
    (uuid, event) => {
      setState((prev) => {
        let index = prev.ncr.order.orderItems.findIndex(
          (item) => item.uuid === uuid
        );
        prev.ncr.order.orderItems = [...prev.ncr.order.orderItems];
        prev.ncr.order.orderItems[index] = {
          ...prev.ncr.order.orderItems[index],
          ...event,
        };
        return { ...prev };
      });
    },
    [setState]
  );

  /**
   * FUNCTION: deleteItem
   * deletes a given Item from the Order
   * @param uuid - the unique id for the product we're removing
   **/
  const deleteItem = useCallback(
    (uuid) => {
      setState((prev) => {
        let index = prev.ncr.order.orderItems.findIndex(
          (item) => item.uuid === uuid
        );
        prev.ncr.order.orderItems = [...prev.ncr.order.orderItems];
        prev.ncr.order.orderItems.splice(index, 1);
        return { ...prev };
      });
    },
    [setState]
  );

  const [itemHtml, setItemHtml] = useState([]);

  useEffect(() => {
    if (itemHtml.length !== state.ncr.order.orderItems.length) {
      setItemHtml(
        state.ncr.order.orderItems.map((item) => {
          let uuid = uuidv4();
          item.uuid = uuid;
          return (
            <OrderItem
              id={uuid}
              key={uuid}
              selectedProduct={item}
              selectedQuantity={item.quantity}
              currency={state.currencies[state.ncr.subsidiary?.currency]}
              currencyCode={state.ncr.subsidiary?.currency}
              callBackFunction={(event, name) => updateItem(uuid, event, name)}
              callBackFunctionDelete={() => deleteItem(uuid)}
              disabled={isDisabled}
            />
          );
        })
      );
    }
  }, [
    updateItem,
    deleteItem,
    isDisabled,
    itemHtml.length,
    state.currencies,
    state.ncr.order.orderItems,
    state.ncr.subsidiary?.currency,
    state.products,
  ]);

  /*
    This useEffect is keeping track of the totals
   */
  useEffect(() => {
    const order_total = state.ncr.order.orderItems
      .map((item) => item?.unit_price * item?.quantity || 0)
      .reduce((prev, next) => prev + next, 0);
    const total_cost =
      order_total +
      state.ncr.order.overhead_cost +
      state.ncr.order.transport_cost +
      state.ncr.order.installation_cost;
    setState((prev) => {
      prev.ncr.order.total_cost = total_cost;
      prev.ncr.order.item_cost = order_total;
      return { ...prev };
    });
  }, [
    setState,
    state.ncr.order.orderItems,
    state.ncr.order.overhead_cost,
    state.ncr.order.transport_cost,
    state.ncr.order.installation_cost,
    state.products,
  ]);

  return (
    <>
      <Row className={'mb-2'}>
        <Col>
          <h6>Order Items</h6>
          <hr />
        </Col>
      </Row>
      <Row className={'mb-2'}>
        <Col md>
          <Row className={'mb-2'}>
            <Col xs={3} className={'my-auto'}>
              Item
            </Col>
            <Col xs={4} className={'my-auto'}>
              Description
            </Col>
            <Col xs={2} className={'my-auto'}>
              Quantity
            </Col>
            <Col xs={2} className={'my-auto'}>
              Price
            </Col>
            <Col xs={1} className={'my-auto'}>
              <FormControl className="invisible" />
            </Col>
          </Row>
          <Row className={'mb-2'}>
            <Col>{itemHtml}</Col>
          </Row>
          <Row className={'my-4 text-center'}>
            <Col>
              <Button
                variant="warning"
                disabled={isDisabled}
                onClick={addOrderItem}
              >
                Add Product +
              </Button>
            </Col>
          </Row>
        </Col>
      </Row>
    </>
  );
};

export { OrderItems };
